<script setup lang="ts">
import type { ZipSummary } from "@/models"
import { computed } from "vue"
import { Alert, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"
import { ZipStatus } from "@/models"

const props = withDefaults(defineProps<{
  zipSummary?: ZipSummary
  isGenerating?: boolean
  mode: "delete" | "export"
  showMetadata?: boolean
}>(), {
  isGenerating: false,
  showMetadata: false,
})

const { t } = useTranslation()

const zipState = computed<"ready" | "pending" | "missing">(() => {
  if (props.zipSummary?.downloadUrl)
    return "ready"

  if (props.isGenerating || props.zipSummary?.id)
    return "pending"

  return "missing"
})

const statusLabel = computed(() => {
  if (!props.zipSummary?.status)
    return ""

  switch (props.zipSummary.status) {
    case ZipStatus.Running:
    case ZipStatus.ReadyForZipping:
    case ZipStatus.ReadyForEmailing:
      return t("zip_export_drawer.status_processing")
    case ZipStatus.Complete:
      return t("zip_export_drawer.status_completed")
    case ZipStatus.Expired:
      return t("zip_export_drawer.status_expired")
    case ZipStatus.Deleted:
      return t("zip_export_drawer.status_deleted")
    case ZipStatus.Error:
      return t("zip_export_drawer.status_error")
    default:
      return t("zip_export_drawer.status_pending")
  }
})
</script>

<template>
  <Alert
    v-if="mode === 'export' && zipState === 'pending'"
    prefix-icon="Caution"
    :closable="false"
    class="mb-4"
  >
    {{ t("zip_export_drawer.pending_message") }}
  </Alert>

  <div
    v-else-if="mode === 'delete' && zipState === 'pending'"
    class="flex items-center gap-3 mt-2 mb-6"
    data-test="zip_pending_state"
  >
    <Icon icon="Loading" size="lg" class="min-w-fit" />
    <div class="leading-5">
      <div class="font-semibold">
        {{ t("event_delete_drawer.generating_message") }}
      </div>
      <div>{{ t("event_delete_drawer.email_message") }}</div>
    </div>
  </div>

  <Alert
    v-else-if="mode === 'delete' && zipState === 'missing'"
    prefix-icon="CircleInfo"
    :closable="false"
    class="mt-2 mb-6"
    data-test="zip_missing_state"
  >
    {{ t("event_delete_drawer.missing_zip_message") }}
  </Alert>

  <div v-if="showMetadata && zipSummary" class="mb-8 flex flex-col gap-5">
    <dl class="flex gap-2.5 flex-col font-body">
      <div v-if="zipSummary.expiredAt" class="flex gap-5">
        <dt class="w-[30%]">
          {{ t("zip_export_drawer.download_expires") }}
        </dt>
        <dd class="font-medium">
          {{ zipSummary.expiredAt?.toLocaleDateString() }}
        </dd>
      </div>
      <div v-if="zipSummary.completedAt" class="flex gap-5">
        <dt class="w-[30%]">
          {{ t("zip_export_drawer.zip_completed_at") }}
        </dt>
        <dd class="font-medium">
          {{ zipSummary.completedAt?.toLocaleDateString() }}
        </dd>
      </div>
      <div v-if="zipSummary.status && statusLabel" class="flex gap-5">
        <dt class="w-[30%]">
          {{ t("status") }}
        </dt>
        <dd class="font-medium">
          {{ statusLabel }}
        </dd>
      </div>
    </dl>
  </div>

  <slot :zip-state="zipState" />
</template>
